// Feb 21, 2026 — WithdrawModal.
//
// Smart withdrawal flow: before sending the creator to Stripe Express
// dashboard (where actual payouts live), ask if they want to top up
// their per-silo platform credit voucher first. Pre-fills with the
// recommended amount based on each silo's last-30-day burn.
//
// Two paths:
//   - "Top up first" → opens VoucherTopupModal
//   - "Open Stripe dashboard" → real <a target="_blank"> to Express
//
// Feb 21, 2026 (late eve) — iOS Safari popup-blocker bug fix.
// `window.open()` called AFTER an `await` is no longer treated as a user
// gesture, so Safari either blocks the popup silently OR (when we had a
// `window.location.href` fallback) navigated the CURRENT tab to Stripe —
// which Randy saw as "a white page" because his My Silo tab was being
// replaced by Stripe's loading screen. The cure: pre-fetch the dashboard
// URL when the modal opens, then render the CTA as a real <a target="_blank">.
// A direct anchor click on a fully-formed href doesn't trip popup blockers
// on any browser, on any device.
import { useEffect, useState } from "react";
import { X, Bank, Sparkle, ArrowSquareOut, CurrencyDollar, Info, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fmtUSD = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

export default function WithdrawModal({ open, onClose, onOpenTopup }) {
  const [voucher, setVoucher] = useState(null);
  const [stripeUrl, setStripeUrl] = useState(null);
  const [stripeErr, setStripeErr] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setStripeUrl(null);
    setStripeErr(null);
    (async () => {
      // Voucher summary (non-blocking — modal still works without it)
      try {
        const r = await api.get("/me/voucher-summary");
        if (!cancelled) setVoucher(r.data);
      } catch { /* silent */ }
      // Pre-fetch Stripe Express dashboard URL so the user gets a real
      // <a target="_blank"> to click on — no popup-blocker risk.
      try {
        const r = await api.post("/stripe/connect/dashboard");
        if (!cancelled) {
          if (r.data?.url) setStripeUrl(r.data.url);
          else setStripeErr("Couldn't fetch dashboard URL.");
        }
      } catch (e) {
        if (!cancelled) setStripeErr(e.response?.data?.detail || "Couldn't fetch dashboard URL.");
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  if (!open) return null;

  // Total recommended top-up across all silos with non-zero recent burn.
  const recommendedTotal = voucher?.silos?.reduce((acc, s) => {
    return acc + (s.spent_last_30d_cents > 0 ? Math.max(s.recommended_topup_cents, 500) : 0);
  }, 0) || 0;
  const silosNeedingTopup = voucher?.silos?.filter((s) => s.spent_last_30d_cents > 0 || s.voucher_cents < 500) || [];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose} data-testid="withdraw-modal">
      <div className="modal-safe bg-[#FDFCF8] rounded-2xl w-full max-w-lg shadow-2xl border hairline"
        onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b hairline flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bank size={18} weight="duotone" className="text-[#4A5D4E]" />
            <h2 className="font-serif-display text-xl text-[#1C1B1A]">Withdraw earnings</h2>
          </div>
          <button onClick={onClose} data-testid="withdraw-modal-close"
            className="p-1 rounded hover:bg-[#F0F1EE] text-[#6B6A68] hover:text-[#1C1B1A]">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="text-sm text-[#1C1B1A] leading-relaxed">
            Your creator earnings (fan subs + tips) and Partner Program rev-share auto-route to your <strong>Stripe Express</strong> account. Withdraw to your bank from there.
          </div>

          {recommendedTotal > 0 && silosNeedingTopup.length > 0 && (
            <div className="bg-[#F8F4E8] border border-[#E5C77F] rounded-md p-3" data-testid="withdraw-topup-nudge">
              <div className="flex items-start gap-2">
                <Sparkle size={16} weight="duotone" className="text-[#C19A4A] shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-[#1C1B1A]">Before you withdraw — top up your platform credit?</div>
                  <div className="text-[11px] text-[#6B6A68] mt-1 leading-relaxed">
                    Based on the last 30 days, <strong>{fmtUSD(recommendedTotal)}</strong> across {silosNeedingTopup.length} silo{silosNeedingTopup.length === 1 ? "" : "s"} would keep Silo Wars, non-Gemini models, and platform-side refinery running. Skip if you'd rather just withdraw.
                  </div>
                  <button
                    onClick={() => { onOpenTopup?.(); onClose(); }}
                    data-testid="withdraw-topup-btn"
                    className="mt-2 text-[11px] px-3 py-1.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5"
                  >
                    <CurrencyDollar size={11} weight="bold" /> Top up silos first
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="bg-[#F0F1EE] border hairline rounded-md p-3 text-[11px] text-[#4A5D4E] flex gap-2" data-testid="withdraw-stripe-info">
            <Info size={14} weight="duotone" className="shrink-0 mt-0.5" />
            <span>
              <strong>What you&apos;ll see:</strong> your Stripe Express (creator-side) dashboard — fan-sub destination charges + tips routed here. Gross there is what the supporter paid (your price plus their service fee); the fee is pulled back out, leaving your price. <em>Platform-level money</em> (creator subs + Creator Pro purchases + collected service fees) lives on a separate Stripe surface — access it from <strong>Super Admin → Financials → Open platform dashboard</strong>.
            </span>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} data-testid="withdraw-cancel-btn"
              className="text-xs px-3 py-2 rounded border hairline text-[#6B6A68] hover:text-[#1C1B1A]">
              Cancel
            </button>
            {/* Real <a> with target="_blank" — no popup-blocker risk.
                Disabled until URL is pre-fetched. */}
            {stripeUrl ? (
              <a
                href={stripeUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => { setTimeout(onClose, 100); }}
                data-testid="withdraw-stripe-btn"
                className="text-xs px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] inline-flex items-center gap-1.5"
              >
                <Bank size={12} weight="bold" />
                Open Stripe dashboard
                <ArrowSquareOut size={10} />
              </a>
            ) : (
              <button
                type="button"
                disabled
                data-testid="withdraw-stripe-btn-loading"
                className="text-xs px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] inline-flex items-center gap-1.5 opacity-50 cursor-not-allowed"
              >
                <Spinner size={12} className="animate-spin" />
                {stripeErr ? "Couldn't load — try again" : "Preparing link…"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
